import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cart_items');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('cart_items', JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((series) => {
    setItems(prev => {
      if (prev.find(i => i.id === series.id)) return prev;
      return [...prev, {
        id: series.id,
        title: series.title,
        price: series.price || 0,
        discount_price: series.discount_price || 0,
        thumbnail_url: series.thumbnail_url || '',
        category_name: series.category_name || ''
      }];
    });
  }, []);

  const removeItem = useCallback((seriesId) => {
    setItems(prev => prev.filter(i => i.id !== seriesId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback((seriesId) => {
    return items.some(i => i.id === seriesId);
  }, [items]);

  const getTotal = useCallback(() => {
    return items.reduce((sum, item) => {
      const price = (item.discount_price > 0 && item.discount_price < item.price) ? item.discount_price : item.price;
      return sum + price;
    }, 0);
  }, [items]);

  const itemCount = items.length;

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, isInCart, getTotal, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
